/*
  SLIMZ — Reviews.

  Sorting and "show all", nothing else. Every review is server-rendered, so the
  wall is complete and readable with JS off; this only reorders and reveals what
  is already in the DOM. No fetching, no pagination round-trips.

  Default sort is FEATURED (M22z2): 4★ and 5★ first by date, then the rest by date.
  Not highest-rated (§8.1): a best-first wall on a new store reads as fake, and the
  mixed reviews are load-bearing — they are all still on the page.

  Original code. Vanilla custom element, no dependencies.
*/
(() => {
  if (customElements.get('slimz-reviews')) return;

  class SlimzReviews extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-slimz-rev-list]');
      this.sortEl = this.querySelector('[data-slimz-rev-sort]');
      this.moreBtn = this.querySelector('[data-slimz-rev-more]');
      if (!this.list) return;

      this.items = Array.from(this.list.querySelectorAll('[data-slimz-rev-item]'));
      this.initial = parseInt(this.dataset.initial, 10) || 6;
      this.expanded = false;

      if (this.sortEl) this.sortEl.addEventListener('change', () => this.sort());
      if (this.moreBtn) {
        this.moreBtn.addEventListener('click', () => {
          this.expanded = true;
          this.moreBtn.hidden = true;
          this.applyVisibility();
          // Move focus to the first newly revealed card so a keyboard user
          // lands where the content appeared rather than at the top.
          const revealed = this.items[this.initial];
          if (revealed) {
            revealed.setAttribute('tabindex', '-1');
            revealed.focus();
          }
        });
      }

      // Sort on init, not only on change (2026-09-04). The Liquid emits the
      // metaobject list in STORAGE order — the rating-band interleave that
      // reorder_reviews.py writes — so with the select reading "Most recent"
      // the first card on screen was a Jan 2024 review while Apr–Aug 2026
      // reviews sat further down. The wall looked abandoned; it was unsorted.
      if (this.sortEl && this.items.length > 1) this.sort();
      else this.applyVisibility();
    }

    sort() {
      const mode = this.sortEl.value;
      const by = {
        // M22z2 (Alon, 2026-09-14): the Tank Top wall opened on two 3-star reviews.
        // "Featured" = 4 and 5 stars first, newest first, then the rest, newest first.
        // Still every review, still mixed 4s among the 5s at the top — not a
        // best-first wall (§8.1) — and "Most recent" / "Lowest rated" stay one tap away.
        featured: (a, b) =>
          (this.num(b, 'rating') >= 4) - (this.num(a, 'rating') >= 4) || this.num(b, 'date') - this.num(a, 'date'),
        recent: (a, b) => this.num(b, 'date') - this.num(a, 'date'),
        high: (a, b) => this.num(b, 'rating') - this.num(a, 'rating'),
        low: (a, b) => this.num(a, 'rating') - this.num(b, 'rating'),
      }[mode];
      if (!by) return;
      this.items.sort(by);
      this.items.forEach((el) => this.list.appendChild(el));
      this.applyVisibility();
    }

    num(el, key) {
      return parseInt(el.dataset[key], 10) || 0;
    }

    applyVisibility() {
      this.items.forEach((el, i) => {
        el.hidden = !this.expanded && i >= this.initial;
      });
    }
  }

  customElements.define('slimz-reviews', SlimzReviews);
})();
